import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress } from '@mui/material';
import { getUser } from 'redux/slices/authSlice';
import { checkImportStatus, setICalendarStoreKeyForImporting } from 'redux/slices/organizationSlice';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ItemSettings from '../components/ItemSettings';
import { calendarApiV3 as api } from 'api';
import ICalendarIntegrationV3 from './ICalendarIntegrationV3';

import useStylesSettings from '../styles/useStylesSettings.styles';

const ICalendarSettingsV3 = (props) => {
    const {
        toggleChange,
        icalendarIntegration,
        organizationId,
        isMigrating
    } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const { classes } = useStylesSettings();
    const [pendingImport, setPendingImport] = useState(true);

    const handleCheckChange = (event, settingName) => {
        toggleChange(settingName, event.target.checked);
    };

    const isVisible = useCallback(() => {
        if (user?.role === 'root') return true;
        if (user?.role === 'admin' && icalendarIntegration) return true;
        return false;
    }, [user, icalendarIntegration]);

    // Check the import status
    // and set the pendingImport state accordingly
    const importStatus = useCallback(() => {
        if (isMigrating) {
            return;
        }
        
        if (!pendingImport) return;
        console.log('Executing importStatus');
        dispatch(
            checkImportStatus(
                organizationId,
                () => setPendingImport(false),
                () => setPendingImport(false),
            ),
        );
    }, [dispatch, organizationId, pendingImport, isMigrating]);

    useEffect(() => {
        if (isMigrating) {
            return;
        }
        importStatus();

        const intervalId = setInterval(importStatus, 60000);

        return () => clearInterval(intervalId);
    }, [importStatus, isMigrating]);

    const handleImportData = () => {
        api.get(`importer/organizations/${organizationId}/import-data-from-v2`)
            .then((response) => {
                dispatch(setICalendarStoreKeyForImporting(response.data));
                setPendingImport(true);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    if (isMigrating) return null;

    return (
        <>
            {isVisible() && (
                <ICalendarIntegrationV3 organizationId={organizationId} />
            )}

            {/* Configuraciones específicas de V3 */}
            {user?.role === 'root' && (
                <>
                    <div className={classes.pb10px}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: '100%' }}
                        >
                            <Box sx={{ display: 'inline-flex' }}>
                                <Button
                                    variant="contained"
                                    className={classes.redColor}
                                    size="small"
                                    onClick={handleImportData}
                                    disabled={pendingImport}
                                >
                                    {pendingImport ? (
                                        <CircularProgress
                                            style={{ width: '80%', height: 'auto' }}
                                            color="secondary"
                                        />
                                    ) : (
                                        t('Import data')
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </div>

                    <ItemSettings
                        label={t('profile.settings.iCalendar.icalendarIntegration')}
                        onChange={handleCheckChange}
                        name={'icalendarIntegration'}
                        keySettings={SettingsKey.ICALENDAR_INTEGRATION}
                        value={icalendarIntegration}
                        paddingBottom
                    />
                </>
            )}
        </>
    );
};

export default ICalendarSettingsV3;